"use client";
import { useState, useMemo } from "react";
import { Box, InputAdornment, IconButton } from '@mui/material';
import axios from 'axios';
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from '../../provider';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import TextInput from '../../components/Forms/TextInput';
import ButtonForm from "../../components/Button/ButtonForm";
import FormAlert from "../../components/Alert/FormAlert";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from '../../language';

const LOGIN = 'https://www.23files.com/api/auth/login';

const createValidationSchema = (t) => Yup.object().shape({
  username: Yup.string().required(t("val_username_req")),
  password: Yup.string().required(t("val_pw_req")),
});

const LoginForm = () => {

  const { t, language } = useTranslation();

  const validationSchema = useMemo(() => createValidationSchema(t), [language]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePassword = () => { setShowPassword(!showPassword); };

  const { handleLogin } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const existing          = location.state?.from?.pathname || "/CockpitWelcome";
  const onb_subscription  = location.state?.from?.pathname || "/OnbSubscription";
  const onb_labadmin      = location.state?.from?.pathname || "/FirstLabvisit";
  const onb_addon         = location.state?.from?.pathname || "/AddonSelection";
  const onb_questionnaire = location.state?.from?.pathname || "/NewQuestionnaire";
  const onb_labvisit      = location.state?.from?.pathname || "/LabvisitStep";
  const onb_waiting       = location.state?.from?.pathname || "/FirstLoginStep";
  const onb_microbiome    = location.state?.from?.pathname || "/MicrobiomeSampleStep";

  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (values, formikHelpers) => {
    try {
      setErrorMessage(null);
      const response = await axios.post(LOGIN, values);

      handleLogin({ user: response.data.user, token: response.data.token });

      const status = response.data.user.onboarding_status;
      if      (status === 1) navigate(onb_subscription,         { replace: true });
      else if (status === 2) navigate(onb_labadmin,              { replace: true });
      else if (status === 3) navigate(onb_addon,                 { replace: true });
      else if (status === 4) navigate(onb_questionnaire,         { replace: true });
      else if (status === 5) navigate(onb_labvisit,              { replace: true });
      else if (status === 6) navigate(onb_waiting,               { replace: true });
      else if (status === 41 || status === 42) navigate(onb_microbiome, { replace: true });
      else                   navigate(existing,                  { replace: true });

    } catch (error) {
      if (error.response?.data) {
        setErrorMessage({ message: t("val_login_invalid") });
      } else {
        setErrorMessage({ message: t("val_server_error") });
      }
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <>
      {errorMessage && (
        <FormAlert
          severity="error"
          title={t("val_form_error")}
          message={errorMessage.message}
          onClose={() => setErrorMessage(null)}
          sx={{ mt: 2 }}
        />
      )}

      <Formik
        initialValues={{ username: '', password: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <TextInput label={t("login_username")} name="username" type="text" placeholder="Username" />
            <TextInput
              label={t("login_pw")}
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              InputProps={{
                title: undefined,
                autoComplete: 'current-password',
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <ButtonForm name={t("login_signin")} disabled={isSubmitting} />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default LoginForm;
